'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { Globals, useReducedMotion } from 'react-spring';

const LAGRINGSNYCKEL = 'presentation-mindre-rorelse';

/**
 * Reglagets val, läst som en extern källa i stället för via en effekt.
 *
 * localStorage finns inte under serverrendering, så en lat useState-initierare hade
 * gett hydreringsfel. useSyncExternalStore har en egen serverögonblicksbild och löser
 * det utan att synka state i en effekt.
 */
const lyssnare = new Set<() => void>();

function prenumerera(påMinnelse: () => void) {
    lyssnare.add(påMinnelse);
    window.addEventListener('storage', påMinnelse);
    return () => {
        lyssnare.delete(påMinnelse);
        window.removeEventListener('storage', påMinnelse);
    };
}

function hämtaKlient(): boolean | null {
    try {
        const sparat = window.localStorage.getItem(LAGRINGSNYCKEL);
        return sparat === 'true' ? true : sparat === 'false' ? false : null;
    } catch {
        // Privat läge eller blockerad lagring — standardvärdet duger.
        return null;
    }
}

const hämtaServer = (): boolean | null => null;

function skriv(värde: boolean) {
    try {
        window.localStorage.setItem(LAGRINGSNYCKEL, String(värde));
    } catch {
        // Lagring är en bekvämlighet, inte ett krav.
    }
    lyssnare.forEach((l) => l());
}

type RörelseVärde = {
    /** Sant när rörelsen ska stå still — springsen landar direkt på sitt to-värde. */
    mindreRörelse: boolean;
    /** Sant när systemet självt begärt mindre rörelse. Reglaget kan inte tvinga bort den tyst. */
    systemetBegär: boolean;
    sättManuellt: (värde: boolean) => void;
};

const Kontext = createContext<RörelseVärde>({
    mindreRörelse: false,
    systemetBegär: false,
    sättManuellt: () => {}
});

/**
 * Reduced motion som förstahandsväg.
 *
 * `useReducedMotion()` är ingen passiv läsning — den sätter `Globals.skipAnimation`,
 * vilket får varje spring i trädet att landa direkt på sitt `to`. Därför behöver ingen
 * enskild komponent ett if: texten renderas, siffran visas, korten ligger på plats.
 * De anländer bara redan framme.
 *
 * Reglaget finns för att vägen ska gå att verifiera utan att någon ändrar sina
 * macOS-inställningar — det är så reduced-motion-vägar annars ruttnar. Systemets
 * begäran vinner alltid: reglaget kan slå PÅ mindre rörelse, aldrig av den.
 *
 * OBS: `Globals` är modulglobal och följer med vid klientnavigering vidare in i appen.
 * Det är avsiktligt — användarens uttalade preferens ska gälla överallt — men det
 * betyder också att flaggan överlever att den här providern avmonteras.
 */
export function RörelseProvider({ children }: { children: ReactNode }) {
    const systemet = useReducedMotion();
    const manuellt = useSyncExternalStore(prenumerera, hämtaKlient, hämtaServer);
    const sättManuellt = useCallback((nytt: boolean) => skriv(nytt), []);

    const systemetBegär = systemet === true;
    const mindreRörelse = systemetBegär || manuellt === true;

    useEffect(() => {
        Globals.assign({ skipAnimation: mindreRörelse });
        return () => Globals.assign({ skipAnimation: false });
    }, [mindreRörelse]);

    const värde = useMemo<RörelseVärde>(
        () => ({ mindreRörelse, systemetBegär, sättManuellt }),
        [mindreRörelse, systemetBegär, sättManuellt]
    );

    return <Kontext.Provider value={värde}>{children}</Kontext.Provider>;
}

export function useRörelse(): RörelseVärde {
    return useContext(Kontext);
}
